import React, { useState, useEffect } from "react";
import logo from "../assets/images/Buddy-4-Travel_logo.png";
import { NavLink } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { Login } from "../Redux/Action/AuthAction";
import travelImages from "../assets/images/login-bg.png";
import {
  isPasskeySupported,
  isPlatformAuthenticatorAvailable,
  authenticateWithPasskey,
  checkPasskeyAvailability,
} from "../services/passkeyService";
import { toast } from "react-toastify";

export default function LoginPage() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passkeySupported, setPasskeySupported] = useState(false);
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [showPasskeyOption, setShowPasskeyOption] = useState(false);

  useEffect(() => {
    checkPasskeySupport();
  }, []);

  const checkPasskeySupport = async () => {
    const supported = isPasskeySupported();
    setPasskeySupported(supported);

    if (supported) {
      const available = await isPlatformAuthenticatorAvailable();
      setPasskeyAvailable(available);
    }
  };

  const checkEmailForPasskey = async (email) => {
    console.log("Checking email for passkey:", email);
    console.log("Passkey support:", { passkeySupported, passkeyAvailable });

    if (!email || !passkeySupported || !passkeyAvailable) {
      console.log("Passkey check skipped - requirements not met");
      return;
    }

    try {
      console.log("Calling checkPasskeyAvailability...");
      const hasPasskey = await checkPasskeyAvailability(email);
      console.log("Passkey availability result:", hasPasskey);
      setShowPasskeyOption(hasPasskey);
    } catch (error) {
      console.error("Error checking passkey availability:", error);
      setShowPasskeyOption(false);
    }
  };

  const handlePasskeyLogin = async () => {
    if (!formik.values.email) {
      toast.error("Please enter your email first");
      return;
    }

    setPasskeyLoading(true);
    try {
      const result = await authenticateWithPasskey(formik.values.email);
      if (result.status_code === 200) {
        localStorage.setItem("authorization", result.data.token);
        window.location.href = result.data.permission[0].url;
        toast.success("Login successful!");
      } else {
        toast.error(result.message || "Passkey authentication failed");
      }
    } catch (error) {
      console.error("Passkey login error:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error("Passkey authentication failed");
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object().shape({
      email: Yup.string().email().required("Email is required"),
      password: Yup.string().required("Password is required"),
    }),
    onSubmit: async (value) => {
      setLoading(true);
      try {
        const result = await dispatch(Login(value));
        if (result?.success && result?.token) {
          localStorage.setItem("authorization", result.token);
        }
      } catch (err) {
        console.log(err);
      }
      setLoading(false);
    },
  });

  return (
    <div className="b4t-login min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-10 relative overflow-hidden">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,500&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500&display=swap');

        .b4t-login {
          background: radial-gradient(1200px 600px at 15% 10%, #103b30 0%, #0a2b22 45%, #071f19 100%);
          font-family: 'Plus Jakarta Sans', ui-sans-serif, system-ui, sans-serif;
        }
        .b4t-display { font-family: 'Fraunces', ui-serif, Georgia, serif; }
        .b4t-mono { font-family: 'IBM Plex Mono', ui-monospace, monospace; }

        .b4t-ticket {
          background: #0e2e26;
        }

        .b4t-perforation {
          display: none;
        }
        .b4t-perforation span {
          width: 14px;
          height: 14px;
          border-radius: 9999px;
          background: #0a2b22;
          flex-shrink: 0;
        }
        @media (min-width: 1024px) {
          .b4t-perforation {
            position: absolute;
            top: 0;
            bottom: 0;
            left: calc(51.25% - 14px);
            width: 28px;
            display: flex;
            flex-direction: column;
            justify-content: space-evenly;
            align-items: center;
            z-index: 20;
          }
        }

        .b4t-flight-path {
          stroke-dasharray: 6 10;
          animation: b4t-dash 18s linear infinite;
        }
        .b4t-plane {
          animation: b4t-fly 6s ease-in-out infinite;
          transform-origin: center;
        }
        @keyframes b4t-dash {
          to { stroke-dashoffset: -320; }
        }
        @keyframes b4t-fly {
          0%, 100% { transform: translate(0, 0) rotate(-8deg); }
          50% { transform: translate(4px, -6px) rotate(-8deg); }
        }
        .b4t-card-enter {
          animation: b4t-rise 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        @keyframes b4t-rise {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @media (prefers-reduced-motion: reduce) {
          .b4t-flight-path, .b4t-plane, .b4t-card-enter {
            animation: none !important;
          }
        }
      `}</style>

      {/* ambient background texture */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle, #ffffff 1px, transparent 1px)",
          backgroundSize: "26px 26px",
        }}
      />

      <div className="b4t-card-enter relative z-10 w-full max-w-5xl">
        <div className="relative grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] rounded-[28px] overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.45)] border border-white/10">
          {/* LEFT — journey panel (desktop only) */}
          <div className="b4t-ticket relative hidden lg:flex flex-col justify-between p-10 xl:p-12 text-white overflow-hidden">
            <img
              src={travelImages}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-15"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#0e2e26]/95 via-[#0e2e26]/90 to-[#071f19]/95" />

            <div className="relative">
              <img
                src={logo}
                alt="Buddy 4 Travel"
                className="h-10 w-auto object-contain mb-14"
              />

              <p className="b4t-mono text-[11px] tracking-[0.25em] text-emerald-300/80 uppercase mb-4">
                Admin Access · Boarding Pass
              </p>
              <h2 className="b4t-display text-[2.35rem] xl:text-[2.6rem] leading-[1.08] font-semibold mb-5">
                Every trip starts
                <br />
                with a sign in.
              </h2>
              <p className="text-emerald-100/70 text-[15px] leading-relaxed max-w-[340px]">
                Manage itineraries, bookings and travellers from a single
                console built for the Buddy 4 Travel team.
              </p>
            </div>

            {/* flight path illustration */}
            <div className="relative my-8">
              <svg
                viewBox="0 0 340 90"
                fill="none"
                className="w-full h-auto"
                aria-hidden="true"
              >
                <path
                  d="M10 70 C 90 10, 180 90, 330 20"
                  stroke="#34D399"
                  strokeOpacity="0.55"
                  strokeWidth="2"
                  className="b4t-flight-path"
                />
                <circle cx="10" cy="70" r="4" fill="#34D399" />
                <circle cx="170" cy="55" r="4" fill="#F2C572" />
                <circle cx="330" cy="20" r="4" fill="#34D399" />
                <g className="b4t-plane">
                  <path
                    d="M168 50 L182 55 L168 60 L171 55 Z"
                    fill="#F2C572"
                  />
                </g>
              </svg>
            </div>

            {/* boarding-pass meta strip */}
            <div className="relative grid grid-cols-4 gap-4 pt-6 border-t border-dashed border-emerald-100/20">
              {[
                ["FROM", "You"],
                ["TO", "Console"],
                ["GATE", "24"],
                ["SEAT", "Admin"],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="b4t-mono text-[10px] tracking-[0.2em] text-emerald-300/60 mb-1">
                    {label}
                  </p>
                  <p className="text-sm font-semibold text-white/90">
                    {value}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* perforation seam, desktop only */}
          <div className="b4t-perforation">
            {Array.from({ length: 12 }).map((_, i) => (
              <span key={i} />
            ))}
          </div>

          {/* RIGHT — form panel */}
          <div className="relative bg-white px-6 py-10 sm:px-10 sm:py-12 lg:p-12 xl:p-14 flex flex-col">
            {/* compact header for mobile / tablet */}
            <div className="flex lg:hidden items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0">
                <img
                  src={logo}
                  alt="Buddy 4 Travel"
                  className="w-7 h-7 object-contain"
                />
              </div>
              <div>
                <p className="b4t-mono text-[10px] tracking-[0.2em] text-emerald-600 uppercase">
                  Admin Access
                </p>
                <p className="b4t-display text-xl font-semibold text-gray-900">
                  Buddy 4 Travel
                </p>
              </div>
            </div>

            <div className="mb-8">
              <p className="b4t-mono hidden lg:block text-[11px] tracking-[0.25em] text-emerald-600 uppercase mb-3">
                Welcome back
              </p>
              <h1 className="b4t-display text-3xl sm:text-[2.15rem] font-semibold text-gray-900 mb-2">
                Admin Portal
              </h1>
              <p className="text-gray-500 text-[15px]">
                Enter your credentials to continue to the console.
              </p>
            </div>

            <form onSubmit={formik.handleSubmit} className="space-y-5 flex-1">
              {/* Email */}
              <div className="space-y-2">
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-700"
                >
                  Email address
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400 group-focus-within:text-emerald-500 transition-colors"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                      />
                    </svg>
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className={`w-full pl-11 pr-4 py-3.5 bg-gray-50 border rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 transition-all outline-none ${
                      formik.submitCount > 0 && formik.errors.email
                        ? "border-red-300 bg-red-50/30"
                        : "border-gray-200"
                    }`}
                    placeholder="name@company.com"
                    onChange={formik.handleChange}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      checkEmailForPasskey(e.target.value);
                    }}
                    value={formik.values.email}
                    autoComplete="username"
                    autoFocus
                  />
                </div>
                {formik.submitCount > 0 && formik.errors.email && (
                  <p className="text-xs text-red-600 flex items-center font-medium">
                    <svg
                      className="w-3.5 h-3.5 mr-1 shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {formik.errors.email}
                  </p>
                )}
              </div>

              {/* Password */}
              <div className="space-y-2">
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold text-gray-700"
                >
                  Password
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400 group-focus-within:text-emerald-500 transition-colors"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    className={`w-full pl-11 pr-12 py-3.5 bg-gray-50 border rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 transition-all outline-none ${
                      formik.submitCount > 0 && formik.errors.password
                        ? "border-red-300 bg-red-50/30"
                        : "border-gray-200"
                    }`}
                    placeholder="••••••••••"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.password}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-emerald-600 transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {formik.submitCount > 0 && formik.errors.password && (
                  <p className="text-xs text-red-600 flex items-center font-medium">
                    <svg
                      className="w-3.5 h-3.5 mr-1 shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {formik.errors.password}
                  </p>
                )}
              </div>

              {/* Passkey option */}
              {showPasskeyOption && passkeySupported && passkeyAvailable && (
                <div className="pt-1">
                  <div className="relative mb-5">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-100" />
                    </div>
                    <div className="relative flex justify-center">
                      <span className="b4t-mono px-3 bg-white text-[10px] tracking-[0.2em] text-gray-400 uppercase">
                        Security option
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handlePasskeyLogin}
                    disabled={passkeyLoading || !formik.values.email}
                    className="w-full group flex items-center justify-center gap-3 py-3.5 px-4 bg-blue-50 border border-blue-100 text-blue-700 rounded-2xl font-bold hover:bg-blue-100 transition-all disabled:opacity-50"
                  >
                    {passkeyLoading ? (
                      <svg
                        className="animate-spin h-5 w-5 text-blue-600"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    ) : (
                      <svg
                        className="h-5 w-5 group-hover:scale-110 transition-transform"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                        />
                      </svg>
                    )}
                    {passkeyLoading ? "Authenticating..." : "Login with passkey"}
                  </button>
                </div>
              )}

              <div className="flex items-center justify-between pt-1">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded transition-colors"
                  />
                  <label
                    htmlFor="remember-me"
                    className="ml-2 block text-sm text-gray-600"
                  >
                    Remember me
                  </label>
                </div>
                <NavLink
                  to="/forgetpassword"
                  className="text-sm font-bold text-emerald-600 hover:text-emerald-700 transition-colors"
                >
                  Forgot?
                </NavLink>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-emerald-600 to-emerald-500 text-white py-4 px-4 rounded-2xl font-bold text-lg shadow-[0_10px_24px_rgba(5,150,105,0.25)] hover:shadow-[0_14px_28px_rgba(5,150,105,0.32)] hover:brightness-[1.03] transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center"
              >
                {loading ? (
                  <svg
                    className="animate-spin h-6 w-6 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                ) : (
                  "Sign in to admin"
                )}
              </button>
            </form>

            {/* footer */}
            <div className="mt-8 pt-6 border-t border-dashed border-gray-200 text-center">
              <p className="b4t-mono text-[10px] tracking-widest text-gray-400">
                © {new Date().getFullYear()} Buddy 4 Travel
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-emerald-100/70">
            Having trouble?{" "}
            <a
              href="#"
              className="text-emerald-300 font-bold hover:text-white transition-colors hover:underline"
            >
              Contact support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}